import React, {FormEvent, Fragment, ReactElement, useState} from 'react';
import {RsnBadge, RsnButton, RsnDialog, RsnInput, useBoolean} from '~/library/ui-kit';
import {Password} from '../../model/Password';
import {AllowedSymbolsDialog} from '../shared/AllowedSymbolsDialog';

export type SetPasswordFormData = {
    password: string;
};

export type SetPasswordIssues = Partial<Record<keyof SetPasswordFormData, string>>;

export type SetPasswordFormOnSubmit = MaybePromise<SetPasswordIssues | void>;

type SetPasswordProps = {
    onSubmit: (password: string) => SetPasswordFormOnSubmit;
    isPending?: boolean;
};

export function SetPasswordForm({onSubmit, isPending}: SetPasswordProps) {
    const passwordHelper = useFeedbackHelper();
    const repeatPasswordHelper = useFeedbackHelper();

    const [serverErrors, setServerErrors] = useState<SetPasswordIssues>();

    const [dialog, {on: open, off: close}] = useBoolean(false);

    const [passwordInput, setPasswordInput] = useState('');
    const onPasswordChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        passwordHelper.signalInteraction();
        setServerErrors(undefined);
        setPasswordInput(event.target.value);
    };

    const [repeatPasswordInput, setRepeatPasswordInput] = useState('');
    const onRepeatPasswordChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        repeatPasswordHelper.signalInteraction();
        setServerErrors(undefined);
        setRepeatPasswordInput(event.target.value);
    };

    const {passwordsDoMatch, passwordIsValid, validations, ...errors} = Password.validatePasswords(
        passwordInput,
        repeatPasswordInput
    );
    const formIsValid = passwordsDoMatch && passwordIsValid;

    const handleSubmit = async () => {
        passwordHelper.signalInteraction();
        repeatPasswordHelper.signalInteraction();

        if (!formIsValid) return;

        const errors = await onSubmit(passwordInput);
        if (errors) {
            setServerErrors(errors);
        }
    };

    return (
        <Fragment>
            <form
                onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit();
                }}
                className={`flex flex-1 flex-col gap-6 p-4`}
            >
                <div className='flex flex-col gap-4'>
                    <small className='fv-text-l-regular text-neutral-90'>
                        Create a secure password using the following requirements:
                    </small>

                    <div className='flex flex-wrap gap-2'>
                        {validations.map((criteria, i) => (
                            <RsnBadge.Md key={i} color={criteria.isMet ? 'success' : 'neutral'}>
                                {criteria.name}
                            </RsnBadge.Md>
                        ))}
                    </div>
                </div>

                <RsnInput.Password
                    {...passwordHelper.getHelperProps(errors.getMainError(open) || serverErrors?.password)}
                    label='Password'
                    onChange={onPasswordChange}
                    value={passwordInput}
                />

                <RsnInput.Password
                    {...repeatPasswordHelper.getHelperProps(errors.getMatchError())}
                    label='Confirm password'
                    onChange={onRepeatPasswordChange}
                    value={repeatPasswordInput}
                />

                <div className='mt-auto'>
                    <RsnButton.Primary disabled={isPending} type='submit'>
                        Save password
                    </RsnButton.Primary>
                </div>
            </form>
            <RsnDialog.Controller isOpen={dialog} onClose={close}>
                <AllowedSymbolsDialog />
            </RsnDialog.Controller>
        </Fragment>
    );
}

function useFeedbackHelper() {
    const [hasInteracted, setHasInteracted] = useState(false);
    const signalInteraction = () => {
        if (!hasInteracted) setHasInteracted(true);
    };
    const getHelperProps = (error: string | ReactElement | undefined) =>
        hasInteracted && {
            helperText: error,
            isInvalid: !!error,
        };
    return {signalInteraction, getHelperProps};
}

type MaybePromise<T> = T | Promise<T>;
